import React from 'react';
import { ThemeProps, useFela } from '../..';
import { WithDmId, WithId } from '../../utils/BaseProps';

import { IconProps } from '../Icon/icon';

type Size = 's' | 'm' | 'l';
type Kind = 'primary' | 'secondary' | 'tertiary' | 'invisible';

export type BaseProps = WithDmId &
  WithId & {
    /**
     * String oder Object, das dem Button-Text zugeordnet wird.
     */
    children?: React.ReactNode;
    /**
     * Icon Komponente
     */
    icon?: (props: IconProps) => JSX.Element;
    /**
     * String, mit dem die Style-Variante gewählt werden kann.
     */
    kind?: Kind;
    /**
     * String, mit dem die Größe des Buttons gewählt werden kann.
     */
    size?: Size;
    /**
     * Optional kann das Styling des Buttons überschrieben werden.
     * Davon darf nur im Ausnahmefall Gebrauch gemacht werden!
     */
    style?: object;
  };

type DefaultProps<Tag extends undefined> = BaseProps &
  Omit<
    {
      as?: undefined;
    } & JSX.IntrinsicElements['button'],
    keyof BaseProps
  >;

type NativeProps<Tag extends keyof JSX.IntrinsicElements> = BaseProps &
  Omit<
    {
      /**
       * String, der übergeben werden kann, um ein
       * anderes HTML-Element mit den Eigenschaften eines Buttons zu rendern.
       */
      as?: Tag;
    } & JSX.IntrinsicElements[Tag],
    keyof BaseProps
  >;

type CustomProps<Props extends object> = BaseProps &
  Omit<
    {
      /**
       * Komponente, die übergeben werden kann, um ein
       * anderes Element mit den Eigenschaften eines Buttons zu rendern.
       */
      as: (props: Props) => JSX.Element;
    } & Props,
    keyof BaseProps
  >;

export interface ButtonStyleProps {
  theme: ThemeProps;
  kind: BaseProps['kind'];
  disabled: boolean;
  children: BaseProps['children'];
  icon: BaseProps['icon'];
  size: Size;
  sizes: Record<Size, string>;
}

const buttonSizes: ButtonStyleProps['sizes'] = {
  s: '32px',
  m: '44px',
  l: '48px',
};

const iconSizes = { s: 18, m: 24, l: 30 };

const buttonColors = {
  primary: {
    color: 'color5',
    backgroundColor: 'color2',
    borderColor: 'color2',
    disabled: {
      color: 'color5',
      backgroundColor: 'color10',
      borderColor: 'color10',
    },
    hover: {
      color: 'color2',
      backgroundColor: 'color4',
      borderColor: 'color4',
    },
  },
  secondary: {
    color: 'color2',
    backgroundColor: 'color5',
    borderColor: 'color2',
    disabled: {
      color: 'color10',
      backgroundColor: 'color5',
      borderColor: 'color10',
    },
    hover: {
      color: 'color2',
      backgroundColor: 'color4',
      borderColor: 'color4',
    },
  },
  invisible: {
    color: 'color3',
    disabled: {
      color: 'color10',
    },
    hover: {
      color: 'color2',
    },
  },
} as const;

const defaultStyle = ({
  kind,
  theme,
  disabled,
  children,
  icon,
  size,
}: ButtonStyleProps) => {
  return {
    outline: 'none',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    boxSizing: 'border-box',
    userSelect: 'none',
    appearance: 'none',
    paddingTop: 0,
    paddingBottom: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none',
    cursor: 'pointer',
    textAlign: 'center',
    lineHeight: 1,
    transition:
      'background-color 0.2s, color 0.2s, border-width 0.2s, border-color 0.2s',
    border: `2px solid ${theme.palette[buttonColors.primary.borderColor].hex}`,
    borderRadius: buttonSizes[size],
    paddingLeft: children ? theme.dimension.spacing.s.rem : 0,
    paddingRight: children ? theme.dimension.spacing.s.rem : 0,
    height: buttonSizes[size],
    minHeight: buttonSizes[size],
    width: !children && icon ? buttonSizes[size] : 'auto',
    color: theme.palette[buttonColors.primary.color].hex,
    fontSize: theme.typography.fontSize.m.rem,
    fontFamily: theme.typography.fontFamily.primary.main,
    fontWeight: 500,
    backgroundColor: theme.palette[buttonColors.primary.backgroundColor].hex,
    ':focus': {
      borderWidth: '2px !important',
      boxShadow: `0 0 5px ${theme.palette.color2.hex}`,
      ':hover': {
        borderColor: theme.palette[buttonColors.primary.hover.borderColor].hex,
      },
    },
    ':hover': {
      color: theme.palette[buttonColors.primary.hover.color].hex,
      borderColor: theme.palette[buttonColors.primary.hover.borderColor].hex,
      backgroundColor:
        theme.palette[buttonColors.primary.hover.backgroundColor].hex,
    },
    ':active': {
      transform: 'translateY(1px)',
    },

    extend: [
      {
        condition: kind === 'secondary',
        style: {
          border: `2px solid ${
            theme.palette[buttonColors.secondary.borderColor].hex
          }`,
          color: theme.palette[buttonColors.secondary.color].hex,
          backgroundColor:
            theme.palette[buttonColors.secondary.backgroundColor].hex,
          ':hover': {
            borderColor:
              theme.palette[buttonColors.secondary.hover.borderColor].hex,
            color: theme.palette[buttonColors.secondary.hover.color].hex,
            backgroundColor:
              theme.palette[buttonColors.secondary.hover.backgroundColor].hex,
          },
          ':focus': {
            borderColor: theme.palette[buttonColors.secondary.borderColor].hex,
            borderWidth: '2px !important',

            ':hover': {
              borderColor:
                theme.palette[buttonColors.secondary.hover.borderColor].hex,
            },
          },
        },
      },
      {
        condition: disabled && kind !== 'secondary',
        style: {
          ':disabled': {
            color: theme.palette[buttonColors.primary.disabled.color].hex,
            backgroundColor:
              theme.palette[buttonColors.primary.disabled.backgroundColor].hex,
            borderColor:
              theme.palette[buttonColors.primary.disabled.borderColor].hex,
            cursor: 'not-allowed',
          },
          ':active': {
            transform: 'none',
          },
          ':hover': {
            color: theme.palette[buttonColors.primary.disabled.color].hex,
            backgroundColor:
              theme.palette[buttonColors.primary.disabled.backgroundColor].hex,
            borderColor:
              theme.palette[buttonColors.primary.disabled.borderColor].hex,
          },
        },
      },

      {
        condition: disabled && kind === 'secondary',
        style: {
          ':disabled': {
            border: `2px solid ${
              theme.palette[buttonColors.secondary.disabled.borderColor].hex
            }`,
            color: theme.palette[buttonColors.secondary.disabled.color].hex,
            backgroundColor:
              theme.palette[buttonColors.secondary.disabled.backgroundColor]
                .hex,
            cursor: 'not-allowed',
          },
          ':active': {
            transform: 'none',
          },
          ':hover': {
            border: `2px solid ${
              theme.palette[buttonColors.secondary.disabled.borderColor].hex
            }`,
            color: theme.palette[buttonColors.secondary.disabled.color].hex,
            backgroundColor:
              theme.palette[buttonColors.secondary.disabled.backgroundColor]
                .hex,
          },
        },
      },

      {
        condition: kind === 'invisible',
        style: {
          border: 0,
          color: theme.palette[buttonColors.invisible.color].hex,
          backgroundColor: 'transparent',
          ':hover': {
            color: theme.palette[buttonColors.invisible.hover.color].hex,
            backgroundColor: 'transparent',
          },
          ':focus': {
            border: `2px solid ${theme.palette.color2.hex}`,
          },
        },
      },
    ],
  };
};

function iconContainerStyle({
  theme,
  disabled,
  children,
  size = 'm',
  sizes,
}: ButtonStyleProps) {
  return {
    alignItems: 'center',
    boxSizing: 'content-box',
    display: 'flex',
    flexDirection: 'column',
    cursor: disabled ? 'not-allowed' : 'pointer',
    paddingLeft: children ? theme.dimension.spacing.s.rem : 0,
    width: !children ? sizes[size] : 24,
  };
}

function resolveBaseElementProps(
  { as, disabled, name, onClick, style, type }: NativeProps<'button'>,
  css: any,
) {
  switch (as) {
    case 'button':
      return {
        disabled,
        type,
        className: css([defaultStyle, style]),
        name,
        onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          if (type === 'button') {
            e.preventDefault();
          }
          if (!disabled && onClick) {
            onClick(e);
          }
        },
      };
    default:
      return {
        className: css([defaultStyle, style]),
      };
  }
}

function Button<Tag extends undefined>(props: DefaultProps<Tag>): JSX.Element;
function Button<Tag extends keyof JSX.IntrinsicElements>(
  props: NativeProps<Tag>,
): JSX.Element;
function Button<Props extends object>(props: CustomProps<Props>): JSX.Element;
function Button({
  id,
  as: As = 'button',
  children,
  disabled = false,
  dmId = 'dm-button',
  icon: Icon,
  kind = 'primary',
  name,
  onClick,
  size = 'm',
  sizes = buttonSizes,
  style,
  type = 'button',
  ...otherProps
}: // The implementation signature type needs to be any, because the actual signature is defined by the overloads
// eslint-disable-next-line @typescript-eslint/no-explicit-any
NativeProps<any> | CustomProps<any>): JSX.Element {
  const { css } = useFela({
    kind,
    disabled,
    children,
    icon: Icon,
    size,
    sizes,
  });

  const baseElementProps = resolveBaseElementProps(
    {
      as: As,
      disabled,
      name,
      onClick,
      style,
      type,
    },
    css,
  );

  return (
    <As
      data-designsystem
      data-dmid={dmId}
      id={id || dmId} // todo: dmId should be removed, as soon as id prop is required (myt-7757)
      {...baseElementProps}
      {...otherProps}>
      {children && children}
      {Icon && (
        <div className={css(iconContainerStyle)}>
          <Icon
            id={`${id || dmId}-icon`} // todo: dmId should be removed, as soon as id prop is required (myt-7757)
            size={iconSizes[size as Size]}
            color="currentColor"
          />
        </div>
      )}
    </As>
  );
}

export default Button;
